import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import s from './LeadModal.module.css';

const WA_NUMBER = '972504499737';

const SERVICES = [
  'תכנון אדריכלי לבית פרטי',
  'היתר בנייה',
  'שינויי קבלן / דירת קבלן',
  'שיפוץ והרחבה',
  'עיצוב פנים',
  'לא בטוח/ה - צריך/ה הכוונה',
];

const INITIAL = { name: '', phone: '', service: '', about: '' };

export default function LeadModal({ open, onClose }) {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const firstRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setTimeout(() => firstRef.current?.focus(), 400);

    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setSent(false);
      setSending(false);
      setForm(INITIAL);
    }
  }, [open]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending || sent) return;
    setSending(true);

    const reason = form.about
      ? `${form.service} — ${form.about}`
      : form.service;

    try {
      await fetch('https://dynamic-server-dfc88e1f1c54.herokuapp.com/leads/newLead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: '',
          reason,
          reciver: 'Shovalr00@gmail.com',
        }),
      });
    } catch {
      /* server error — still show success to user */
    }

    setSending(false);
    setSent(true);
    setTimeout(() => {
      onClose();
    }, 3000);
  };

  if (!open) return null;

  return createPortal(
    <div className={s.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true" aria-labelledby="lead-title">

      <div className={s.modal}>
        {/* Close button */}
        <button className={s.close} onClick={onClose} aria-label="סגירה">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Corner marks */}
        <span className={`${s.corner} ${s.cornerTR}`} />
        <span className={`${s.corner} ${s.cornerBL}`} />

        {sent ? (
          <div className={s.success}>
            <div className={s.successIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h3 className={s.successTitle}>הפנייה נשלחה בהצלחה</h3>
            <p className={s.successText}>נחזור אליכם בהקדם</p>
          </div>
        ) : (
          <div className={s.inner}>
            <header className={s.header}>
              <h2 id="lead-title" className={s.title}>בואו נדבר על הבית שלכם</h2>
              <p className={s.sub}>השאירו פרטים ונחזור אליכם לשיחת היכרות קצרה</p>
            </header>

            <form onSubmit={handleSubmit} className={s.form}>
              <div className={s.field}>
                <label className={s.label} htmlFor="lead-name">שם מלא</label>
                <input
                  ref={firstRef}
                  id="lead-name"
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  className={s.input}
                  placeholder="השם שלכם"
                />
              </div>

              <div className={s.field}>
                <label className={s.label} htmlFor="lead-phone">מספר טלפון</label>
                <input
                  id="lead-phone"
                  name="phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={handleChange}
                  className={s.input}
                  placeholder="050-000-0000"
                  dir="ltr"
                />
              </div>

              <div className={s.field}>
                <label className={s.label} htmlFor="lead-service">סוג השירות</label>
                <select
                  id="lead-service"
                  name="service"
                  required
                  value={form.service}
                  onChange={handleChange}
                  className={`${s.input} ${s.select}`}
                >
                  <option value="" disabled>בחרו שירות</option>
                  {SERVICES.map((svc) => (
                    <option key={svc} value={svc}>{svc}</option>
                  ))}
                </select>
              </div>

              <div className={s.field}>
                <label className={s.label} htmlFor="lead-about">קצת על הפרויקט</label>
                <textarea
                  id="lead-about"
                  name="about"
                  value={form.about}
                  onChange={handleChange}
                  className={`${s.input} ${s.textarea}`}
                  placeholder="ספרו לנו בקצרה על מה אתם מחפשים..."
                  rows={3}
                />
              </div>

              <button type="submit" className={`${s.submit} ${sending ? s.submitSending : ''} ${sent ? s.submitSent : ''}`} disabled={sending || sent}>
                <span>{sent ? 'נשלח בהצלחה' : sending ? 'בשליחה...' : 'שליחת פנייה'}</span>
                {!sending && !sent && <span className={s.submitArrow}>←</span>}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
